import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';
import TeamsLogos from '@/components/b2b/TeamsLogos';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import Capabilities from './Capabilities';
import FinalCta from './FinalCta';
import HowItWorks from './HowItWorks';
import { useLandingT, usePageVars } from './kit';
import LandingFaq from './LandingFaq';
import Languages from './Languages';
import PageDetails from './PageDetails';
import type { LandingCopy } from './pages';
import Plans from './Plans';
import Privacy from './Privacy';
import Sources from './Sources';
import TranscriptionHero from './TranscriptionHero';
import UseCases from './UseCases';

/**
 * Every transcription landing page: the upload box and an example first,
 * then what you get, where recordings come from, the kinds of recording,
 * pricing, privacy, languages and questions. Pages differ by `page` only.
 */
export default function TranscriptionLanding({
  page,
  currentLanguage,
}: {
  page: LandingCopy;
  /** Slug of the language page being shown, to mark it in the list. */
  currentLanguage?: string;
}) {
  const { t, lang } = useLandingT();
  const vars = usePageVars(page);

  return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main
      className={clsxm('relative overflow-x-clip', tone.page)}
      dir={lang === 'ar' ? 'rtl' : undefined}
    >
      <Seo
        templateTitle={t(page.seoTitle, vars)}
        description={t(page.seoDescription, vars)}
        image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(
          t(page.ogTitle, vars),
        )}`}
      />
      <TranscriptionHero page={page} />
      <TeamsLogos
        caption={t('Used by people at companies and universities like')}
      />
      <HowItWorks page={page} />
      <PageDetails page={page} />
      <Capabilities />
      <Sources currentPath={page.path} />
      <UseCases currentPath={page.path} />
      <Plans />
      <Privacy />
      <Languages currentSlug={currentLanguage} />
      <LandingFaq page={page} />
      <FinalCta cta={page.cta} />
      <Footer />
    </main>
  );
}
